import React from 'react';
import Breadcrumb from '../../components/breadcrumb';
import UQCard from '../../components/card';
import UQSpacer from '../../components/spacer';
import UQCode from '../../components/code';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';
import { BREADCRUMB_ROOT } from '../../common/config';

const CardDemoPage = () => {
  const attributes = [
    {
      attribute: 'header',
      type: 'string',
      default: 'undefined',
      description: 'The header text for the card.',
    },
    {
      attribute: 'icon',
      type: 'string',
      default: 'undefined',
      description: 'The icon to display in the card header.',
    },
    {
      attribute: 'iconColor',
      type: 'string',
      default: 'undefined',
      description: 'The color of the icon in the card header.',
    },
    {
      attribute: 'loading',
      type: 'boolean',
      default: 'false',
      description: 'Whether the card is in a loading state.',
    },
    {
      attribute: 'loaderProps',
      type: 'UQLoaderProps',
      default: 'undefined',
      description: 'Props to customize the loader (animation, size, color) when loading is true.',
    },
    {
      attribute: 'className',
      type: 'string',
      default: "''",
      description: 'Additional class names for custom styling.',
    },
  ];

  const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
    {
      columnId: 'attribute',
      renderHeaderCell: () => 'Attribute',
      renderCell: (item) => item.attribute,
    },
    {
      columnId: 'type',
      renderHeaderCell: () => 'Type',
      renderCell: (item) => <code>{item.type}</code>,
    },
    {
      columnId: 'default',
      renderHeaderCell: () => 'Default',
      renderCell: (item) => <code>{item.default}</code>,
    },
    {
      columnId: 'description',
      renderHeaderCell: () => 'Description',
      renderCell: (item) => item.description,
    },
  ];

  const defaultCardCode = `
import React from 'react';
import { UQCard } from '@uniqconsulting/ui';

<UQCard header="Default Card" icon="info" iconColor="primary">
  <p>This is a simple card with a header and content.</p>
</UQCard>
`;

  const loadingCardCode = `
import React from 'react';
import { UQCard } from '@uniqconsulting/ui';

<UQCard header="Loading Card" loading>
  <p>This card is in a loading state.</p>
</UQCard>
`;

  const customClassCardCode = `
import React from 'react';
import { UQCard } from '@uniqconsulting/ui';

<UQCard header="Custom Class" className="custom-card" icon="star" iconColor="primary">
  <p>This card has a custom class applied for additional styling.</p>
</UQCard>
`;

  const loaderAnimations = ['spin', 'clip', 'spin-rainbow', 'clip-rainbow'] as const;

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Card']} />
      <h1>UQCard Demo</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQCard</code> component is a versatile container for displaying content. It supports headers, icons, loading states, and custom styling.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
      />
      <UQSpacer size="m" />

      {/* Live Demo */}
      <h2>Live Demo</h2>
      <h3>Default Card</h3>
      <UQSpacer size="s" />
      <UQCard header="Default Card" icon="info" iconColor="primary">
        <p>This is a simple card with a header and content.</p>
      </UQCard>
      <UQSpacer size="s" />
      <UQCode code={defaultCardCode} enableCopy />
      <UQSpacer size="m" />

      <h3>Loading Card</h3>
      <UQSpacer size="s" />
      <UQCard header="Loading Card" loading>
        <p>This card is in a loading state.</p>
      </UQCard>
      <UQSpacer size="s" />
      <UQCode code={loadingCardCode} enableCopy />
      <UQSpacer size="m" />

      <h3>Card with Custom Class</h3>
      <UQSpacer size="s" />
      <UQCard header="Custom Class" className="custom-card" icon="star" iconColor="primary">
        <p>This card has a custom class applied for additional styling.</p>
      </UQCard>
      <UQSpacer size="s" />
      <UQCode code={customClassCardCode} enableCopy />

      <h3>Card with Loader Animation Variations</h3>
      <UQSpacer size="s" />
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        {loaderAnimations.map((animation) => (
          <UQCard
            key={animation}
            header={`Loading (${animation})`}
            loading
            loaderProps={{ animation, size: 'l' }}
          >
            <p>Loading animation: <code>{animation}</code></p>
          </UQCard>
        ))}
      </div>
      <UQSpacer size="s" />
      <UQCode
        code={`<UQCard header="Loading (spin-rainbow)" loading loaderProps={{ animation: 'spin-rainbow', size: 'l' }}>\n  <p>Loading animation: <code>spin-rainbow</code></p>\n</UQCard>`}
        enableCopy
    />
    </>
  );
};

export default CardDemoPage;